"use client";
import React, { useState, useRef, useEffect } from 'react';
import { useEditMode } from './EditModeProvider';
import { Palette, X } from 'lucide-react';
import ColorGradientPicker from './ColorGradientPicker';

const ColorPicker = ({ settings = [], contentKey, defaultColor, onChange, isAbsolute = true, className = '' }) => {
    const { isEditMode, getContent, updateContent } = useEditMode();
    const [isOpen, setIsOpen] = useState(false);
    const popoverRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (popoverRef.current && !popoverRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    if (!isEditMode) return null;

    const isSingle = !!contentKey;
    const singleValue = isSingle ? getContent(contentKey, defaultColor) : null;

    const handleSingleChange = (val) => {
        updateContent(contentKey, val, 'text');
        if (onChange) onChange(val);
    };

    const containerClasses = isAbsolute 
        ? `absolute top-4 right-4 z-50 ${className}` 
        : `relative z-50 ${className}`;

    return (
        <div className={containerClasses} ref={popoverRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="p-2 bg-white dark:bg-gray-800 rounded-full shadow-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center justify-center"
                title="Настройки цветов"
            >
                <Palette className="w-5 h-5 text-gray-700 dark:text-gray-300" />
            </button>

            {isOpen && (
                <div className={`absolute top-full ${isAbsolute ? 'right-0' : 'left-0'} mt-2 w-80 bg-white/95 backdrop-blur-md dark:bg-gray-800/95 rounded-xl shadow-xl border border-gray-200/50 dark:border-gray-700/50 p-4 z-[60]`}>
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="font-semibold text-gray-900 dark:text-white">Настройки цветов</h3>
                        <button onClick={() => setIsOpen(false)} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors">
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                    <div className="space-y-6 max-h-[60vh] overflow-y-auto pr-2 custom-scrollbar">
                        {isSingle ? (
                            <div className="flex flex-col space-y-2">
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Выберите цвет</label>
                                <ColorGradientPicker
                                    value={singleValue}
                                    onChange={handleSingleChange}
                                />
                            </div>
                        ) : (
                            settings?.map((setting) => {
                                const currentValue = getContent(setting.key, setting.default);
                                return (
                                    <div key={setting.key} className="flex flex-col space-y-2">
                                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{setting.label}</label>
                                        <ColorGradientPicker
                                            value={currentValue}
                                            onChange={(val) => updateContent(setting.key, val, 'text')}
                                        />
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ColorPicker;